import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

/**
 * Bank Accounts Modal
 * Shows detailed breakdown of all Thaklis bank accounts
 */
const BankAccountsModal = ({ open, onClose, bankAccounts }) => {
  const formatAmount = (amount) => {
    return `AED ${amount.toLocaleString('en-AE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const totalBalance = bankAccounts.reduce((sum, account) => sum + account.balance, 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <AccountBalanceIcon color="primary" />
            <Typography variant="h6">Thaklis Bank Accounts</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box mb={2} p={2} bgcolor="grey.50" borderRadius={1}>
          <Typography variant="h6" color="primary.main" fontWeight="600" gutterBottom>
            Total Balance: {formatAmount(totalBalance)}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
            {bankAccounts.length} active account{bankAccounts.length !== 1 ? 's' : ''}
          </Typography>
        </Box>

        <TableContainer component={Paper} elevation={0} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#1a7f8f', '& .MuiTableCell-root': { color: 'white' } }}>
                <TableCell sx={{ fontSize: '0.75rem', py: 1 }}><strong>Bank Name</strong></TableCell>
                <TableCell sx={{ fontSize: '0.75rem', py: 1 }}><strong>Account Type</strong></TableCell>
                <TableCell sx={{ fontSize: '0.75rem', py: 1 }}><strong>Account Number</strong></TableCell>
                <TableCell sx={{ fontSize: '0.75rem', py: 1 }}><strong>IBAN</strong></TableCell>
                <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1 }}><strong>Balance</strong></TableCell>
                <TableCell align="center" sx={{ fontSize: '0.75rem', py: 1 }}><strong>Status</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* ACTIVE Accounts */}
              {bankAccounts.filter(acc => acc.status === 'ACTIVE').map((account) => (
                <TableRow key={account.id} hover>
                  <TableCell sx={{ py: 1 }}>
                    <Typography variant="body2" fontWeight="500" fontSize="0.8rem">
                      {account.bankName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontSize="0.65rem" display="block">
                      {account.swiftCode}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Chip
                      label={account.type}
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={{ fontSize: '0.7rem', height: '22px' }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                      {account.accountNumber}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                      {account.iban}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1 }}>
                    <Typography variant="body2" fontWeight="600" color="primary.main" fontSize="0.8rem">
                      {formatAmount(account.balance)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1 }}>
                    <Chip
                      label={account.status}
                      size="small"
                      color="success"
                      sx={{ fontSize: '0.7rem', height: '22px' }}
                    />
                  </TableCell>
                </TableRow>
              ))}

              {/* Separator Row */}
              {bankAccounts.some(acc => acc.status === 'ACTIVE') &&
               bankAccounts.some(acc => acc.status === 'INACTIVE') && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ bgcolor: '#f5f5f5', py: 1, borderTop: '2px solid #ddd', borderBottom: '2px solid #ddd' }}>
                    <Typography variant="caption" fontWeight="600" color="text.secondary" fontSize="0.7rem">
                      INACTIVE ACCOUNTS
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {/* INACTIVE Accounts */}
              {bankAccounts.filter(acc => acc.status === 'INACTIVE').map((account) => (
                <TableRow key={account.id} hover sx={{ opacity: 0.6 }}>
                  <TableCell sx={{ py: 1 }}>
                    <Typography variant="body2" fontWeight="500" fontSize="0.8rem">
                      {account.bankName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontSize="0.65rem" display="block">
                      {account.swiftCode}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Chip
                      label={account.type}
                      size="small"
                      variant="outlined"
                      color="default"
                      sx={{ fontSize: '0.7rem', height: '22px' }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                      {account.accountNumber}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                      {account.iban}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1 }}>
                    <Typography variant="body2" fontWeight="600" color="text.secondary" fontSize="0.8rem">
                      {formatAmount(account.balance)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1 }}>
                    <Chip
                      label={account.status}
                      size="small"
                      color="default"
                      sx={{ fontSize: '0.7rem', height: '22px' }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export default BankAccountsModal;
